import React from 'react';
import { clsx } from 'clsx';
import { useMtmRunMarks } from '@/hooks';
import { formatCurrencyCode } from '@/utils/trades/mtm-calculations';
import { formatMarkPrice } from '@/utils/trades/mark-price-format';
import { netClass, tradeTypePillClass, fxPairLabel, netDirectionLabel } from './history-utils';
import { TradeIdLink } from '../TradeIdLink';

// LR-498: the Stock-L/B instrument table for ONE run — the right-pane line items in MTM
// "By date" (the single selected run) and the inline drill-down under each row of the MTM
// "By counterparty" run ledger. Ported from MtmRunHistory's stock_lb table, but the design
// collapses Price Before / Price After into one "Price (before → after)" column.
//
// Lazy: drives useMtmRunMarks(runId, enabled) so marks are only fetched when the table is
// actually shown (the selected run, or an expanded ledger row).

// LR-577 (feedback #69): both hosts of this table live in History, so the trade-page return
// origin is fixed — unlike RepoCashPoolLedger, which is also mounted by the MTM drawer.
const TRADE_ORIGIN = { from: 'history' } as const;

export interface RunLineItemsProps {
  runId: number;
  /** Run (denomination) currency — formats the Δ columns and the footer. */
  currency: string;
  /** LR-493: COB rate (trade → `currency`) the run was denominated at; drives the cross-ccy footnote.
   *  Absent / "1.000000" ⇒ native run, no footnote. */
  fxRate?: string;
  /** The run's signed net (decimal string or number) — the footer figure and its direction label. */
  net: string | number;
  /** Fetch + render only when visible (selected run / expanded ledger row). */
  enabled?: boolean;
  /** Indent the first column further when nested under a ledger row. */
  nested?: boolean;
}

export const RunLineItems: React.FC<RunLineItemsProps> = ({
  runId,
  currency,
  fxRate,
  net,
  enabled = true,
  nested = false,
}) => {
  const { data: marks, isLoading, error } = useMtmRunMarks(runId, enabled);
  const netNum = Number(net);
  const firstPad = nested ? 'pl-8' : 'pl-6';

  if (isLoading) {
    return (
      <p className={clsx('py-3 pr-4 text-sm text-gray-500', firstPad)}>Loading instruments…</p>
    );
  }
  if (error) {
    return (
      <p className={clsx('py-3 pr-4 text-sm text-red-600', firstPad)}>
        Failed to load instruments.
      </p>
    );
  }
  if (!marks || marks.length === 0) {
    return (
      <p className={clsx('py-3 pr-4 text-sm text-gray-500', firstPad)}>No instruments in this run.</p>
    );
  }

  return (
    <div
      className={clsx(
        'overflow-x-auto',
        // Contained, indented detail panel ONLY when nested under a run-ledger row. As the primary
        // "By date" right-pane content (nested=false) the table stays flush — no orphan left spine.
        nested ? 'border-l-[3px] border-lr-spine bg-lr-panel' : 'bg-white'
      )}
    >
      <table className="w-full table-fixed">
        <colgroup>
          {/* Trade ID is never truncated (see the cell below), so it gets the width to hold an id
              outright — taken from Price, which had the most slack. */}
          <col className="w-[11%]" />
          <col className="w-[21%]" />
          <col className="w-[12%]" />
          <col className="w-[9%]" />
          <col className="w-[20%]" />
          <col className="w-[13.5%]" />
          <col className="w-[13.5%]" />
        </colgroup>
        <thead>
          <tr className="h-[41px] border-b border-[#edf0f3]">
            <th className={clsx('whitespace-nowrap py-2 pr-4 text-left text-[11px] font-semibold uppercase tracking-wide text-lr-faint', firstPad)}>
              Trade ID
            </th>
            <th className="truncate px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-lr-faint">
              Symbol
            </th>
            <th className="truncate px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-lr-faint">Type</th>
            <th className="truncate px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-lr-faint">Qty</th>
            <th className="truncate px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-lr-faint">Price</th>
            {/* LR-538: named to match the MtM preview grid — the same delta_value /
                delta_collateral figures, so one number can't carry two names. */}
            <th className="truncate px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-lr-faint">
              Gross Value Δ
            </th>
            <th className="truncate px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-lr-faint">
              Margin Value Δ
            </th>
          </tr>
        </thead>
        <tbody>
          {marks.map((m) => {
            // LR-493: a stock_lb run is single-currency; when it was denominated in a target
            // currency (Run MTM picked one) the backend carries each row's COB-converted delta in
            // run.currency so the rows still sum to the run net. Native runs have null twins → show
            // the native delta. Either way the figure is in `currency` (the run currency prop).
            const dv =
              m.delta_value_converted != null
                ? Number(m.delta_value_converted)
                : m.delta_value == null
                  ? null
                  : Number(m.delta_value);
            const dc =
              m.delta_collateral_converted != null
                ? Number(m.delta_collateral_converted)
                : m.delta_collateral == null
                  ? null
                  : Number(m.delta_collateral);
            return (
              <tr key={m.id} className="h-[39px] border-b border-gray-200 hover:bg-lr-row-active">
                {/* whitespace-nowrap, NOT truncate: an ellipsised identifier ("104…") reads as a
                    different trade. Parity with the twin column in RepoCashPoolLedger. */}
                <td className={clsx('whitespace-nowrap py-2 pr-4 text-sm', firstPad)}>
                  <TradeIdLink id={m.position_id} origin={TRADE_ORIGIN} />
                </td>
                <td className="truncate px-4 py-2 text-sm text-gray-900">{m.symbol}</td>
                <td className="truncate px-4 py-2 text-sm">
                  <span
                    className={clsx(
                      'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium capitalize',
                      tradeTypePillClass(m.trade_type)
                    )}
                  >
                    {m.trade_type}
                  </span>
                </td>
                <td className="truncate px-4 py-2 text-right text-sm text-gray-900">{m.quantity}</td>
                <td className="truncate px-4 py-2 text-right font-mono text-sm text-gray-400">
                  {m.price_before == null ? '—' : formatMarkPrice(m.price_before)}
                  <span className="mx-1 text-gray-300">→</span>
                  {/* LR-493: suffix the mark currency so the price scale is explicit (a target-ccy
                      run keeps prices in the instrument's own currency, unlike the converted Δ). */}
                  <span className="text-gray-600">{`${formatMarkPrice(m.price_after)} ${m.currency}`}</span>
                </td>
                <td
                  className={clsx(
                    'truncate px-4 py-2 text-right text-sm',
                    dv == null ? 'text-gray-400' : netClass(dv)
                  )}
                >
                  {dv == null ? '—' : formatCurrencyCode(dv, currency)}
                </td>
                <td
                  className={clsx(
                    'truncate px-4 py-2 text-right text-sm',
                    dc == null ? 'text-gray-400' : netClass(dc)
                  )}
                >
                  {dc == null ? '—' : formatCurrencyCode(dc, currency)}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          {/* The Margin Value Δ column sums to the run's signed net — the same figure as the
              run-row glance / hero, and labelled by its sign (LR-577, feedback #68). */}
          <tr className="h-[39px] border-t border-gray-200 bg-lr-panel">
            <td colSpan={5} className="px-4 py-2" />
            <td className="truncate px-4 py-2 text-right text-sm font-medium text-gray-500">
              {netDirectionLabel(netNum)}
            </td>
            <td
              className={clsx(
                'truncate px-4 py-2 text-right text-sm font-semibold',
                netClass(netNum)
              )}
            >
              {formatCurrencyCode(netNum, currency)}
            </td>
          </tr>
        </tfoot>
      </table>
      {/* LR-493: a target-denominated run — the Δ columns are in the run (target) currency but the
          prices stay in the instrument's native currency. Spell out the denomination so the drill-down
          reconciles (parity with the repo cross-settle footnote). Converted twins are the backend's
          target-run marker. */}
      {marks.some((m) => m.delta_value_converted != null) && (
        <p className={clsx('py-1.5 pr-4 text-[11px] text-lr-faint', firstPad)}>
          Marks in {marks[0].currency}; run denominated in {currency} at COB FX{' '}
          {fxPairLabel(marks[0].currency, currency, fxRate)}
        </p>
      )}
    </div>
  );
};
